import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { ClockIcon } from "@heroicons/react/outline";
import {
  clearOngoingConsultations,
  requestListOngoingConsultationByConsultant,
  useAuthState,
  useConsultationDispatch,
  useConsultationState,
} from "../../Context";
import { chatPath } from "../../Utils";

dayjs.extend(relativeTime);

const COUNTDOWN_THRESHOLD_MS = 30 * 60 * 1000;

function endMs(consultation) {
  return consultation.expiredAt
    ? new Date(consultation.expiredAt).getTime()
    : Infinity;
}

function formatCountdown(remainingMs) {
  const clamped = Math.max(remainingMs, 0);
  const minutes = Math.floor(clamped / 60000);
  const seconds = Math.floor((clamped % 60000) / 1000);
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function TimeLeft({ consultation, now }) {
  const { i18n } = useTranslation();

  if (!consultation.expiredAt || endMs(consultation) === Infinity) return null;

  const remaining = endMs(consultation) - now;
  if (remaining <= COUNTDOWN_THRESHOLD_MS) {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-semibold text-red-600">
        <ClockIcon className="h-4 w-4" />
        {formatCountdown(remaining)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-sm text-slate-500">
      <ClockIcon className="h-4 w-4" />
      {dayjs(consultation.expiredAt).locale(i18n.language).fromNow()}
    </span>
  );
}

function OngoingConsultationCardSkeleton() {
  return (
    <div className="flex-shrink-0 w-80 border border-slate-200/60 rounded-lg p-4 animate-pulse">
      <div className="h-5 w-40 bg-gray-300 rounded-md" />
      <div className="mt-2 h-4 w-64 bg-gray-300 rounded-md" />
      <div className="mt-3 flex items-center justify-between">
        <div className="h-4 w-24 bg-gray-300 rounded-md" />
        <div className="h-4 w-16 bg-gray-300 rounded-md" />
      </div>
    </div>
  );
}

/**
 * Horizontal strip of the consultant's ONGOING consultations, soonest-ending
 * first. Consultations ending within 30 minutes show a live countdown.
 */
export function OngoingConsultationList() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { user } = useAuthState();
  const dispatch = useConsultationDispatch();
  const { ongoingConsultations, ongoingLoading } = useConsultationState();

  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    requestListOngoingConsultationByConsultant(dispatch, {
      consultantOwner: user.id,
    });
    return () => {
      clearOngoingConsultations(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ongoingConsultations.length === 0) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [ongoingConsultations.length]);

  if (!ongoingLoading && ongoingConsultations.length === 0) return null;

  const sorted = [...ongoingConsultations].sort((a, b) => endMs(a) - endMs(b));

  return (
    <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm mt-4">
      <div className="p-6">
        <h3 className="mb-4 font-semibold text-lg text-slate-900 tracking-tight">
          {t("ongoingConsultations")}
        </h3>
        <div className="overflow-x-auto">
          <div className="flex gap-4 pb-2" style={{ width: "max-content" }}>
            {ongoingLoading
              ? Array.from({ length: 3 }, (_, index) => (
                  <OngoingConsultationCardSkeleton key={index} />
                ))
              : sorted.map((consultation) => (
                  <div
                    key={consultation.id}
                    onClick={() => navigate(chatPath(consultation))}
                    className="flex-shrink-0 w-80 border border-slate-200/60 rounded-lg p-4 hover:shadow-md hover:border-blue-600/20 transition-all duration-200 cursor-pointer"
                  >
                    <p className="font-medium text-slate-900 truncate">
                      {consultation.description?.name || "-"}
                    </p>
                    <p className="mt-1 text-sm text-slate-500 line-clamp-2">
                      {consultation.description?.description}
                    </p>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-blue-600 truncate">
                        {consultation.category?.name}
                      </span>
                      <TimeLeft consultation={consultation} now={now} />
                    </div>
                  </div>
                ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OngoingConsultationList;
